import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Modal } from 'antd';
import useHTTP from "@/hooks/useHTTP.mjs";
import runAsync from "@/script/runAsync.mjs";
import useDocTitle from '@/hooks/useDocTitle.mjs';
import FileUtils from '@/script/FileUtils';
import SelectSfDialog from '@/pages/MainPage/dialogs/SelectSfDialog';
import Ue from '@/pages/UiDemo/Ue.jsx';
import oprType from '@/pages/SfDemo/OprType.js';
import UcLayout from './UcLayout.jsx';
import SoftwareMap from '@/components/SoftwareMap/SoftwareMap.jsx';

const Pane = () => {
    const [searchParams] = useSearchParams()
    const param = Object.fromEntries(searchParams.entries());

    // 获取URL参数
    const sfId = param.sfId;
    const sfName = param.sfName;
    useDocTitle(sfName);

    const http = useHTTP();
    const ref = useRef({ doOpr: false });

    // 系统功能
    const [rmSfs, setRmSfs] = useState([]);   // 功能
    const [scenes, setScenes] = useState([]); // 场景
    const [sfOprs, setSfOprs] = useState([]); // 操作

    const [sid, setSid] = useState(sfId); // 当前功能
    const [sname, setSname] = useState(sfName);
    const [smap, setSmap] = useState();
    const [scene, setScene] = useState(); // 当前场景
    const [oprId, setOprId] = useState(); // 当前操作
    const [oprTitle, setOprTitle] = useState();
    const [mapPath, setMapPath] = useState(); // 地图节点路径

    // 作业索引、功能索引、场景索引、操作索引
    const [sfIndex, setSfIndex] = useState(0);
    const [sceneIndex, setSceneIndex] = useState(0);
    const [oprIndex, setOprIndex] = useState(0);

    // 界面原型图
    const [ui, setUi] = useState({});
    const [ues, setUes] = useState([]);   // 界面自己的事件
    const [pues, setPues] = useState([]); // 父级界面的实际
    const [uiPics, setUiPics] = useState([]);
    const [uiPicindex, setUiPicIndex] = useState(0);

    // 加载
    const [visibleSf, setVisibleSf] = useState(false);
    const winParam = 'height=500, width=1000, left=300, top=200, toolbar=no, menubar=no, scrollbars=yes,resizable=yes, location=no, status=no';

    // 执行按钮
    const [oprFlag, setOprFlag] = useState(false);

    useEffect(() => {
        loadSf([sfId]);
    }, []);

    const mapInits = {
        selectedNodes: [{path: mapPath}],
    }

    // 加载集成用例
    const loadSf = async (sfIds) => {
        try {
            const data = await runAsync(
                async () => {
                    return await http.get(`/rmsfs/demo`, { pkids: sfIds });
                }, {
                error: { content: (err) => `查询失败: ${err.message}` },
                loading: { show: true },
                success: { show: false },
            });

            const _rmSfs = data.rmSfs ?? [];
            const _sfOprs = data.sfOprs ?? [];

            if (_rmSfs.length == 0) {
                return Modal.error({ content: '集成用例不存在' });
            }

            // 拦截处理
            doOprPrefix(_sfOprs);

            // 取第一个功能
            const firstSf = _rmSfs[0];
            ref.current.prjId = firstSf.prjId;
            ref.current.sVer = firstSf.sVer;

            ref.current.sf = firstSf;
            ref.current.sfId = firstSf.pkid;
            ref.current.sfName = firstSf.sfName;

            ref.current.allSfs = _rmSfs;
            ref.current.allOprs = _sfOprs;

            // 设置功能
            setRmSfs(_rmSfs);
            setSid(firstSf.pkid);
            setSname(firstSf.sfName);
            setSfIndex(0);

            // 构建场景：设置第一个场景、操作
            doCreateScene(firstSf.sfCode);
            doSetFirstScene();

            // 初始化界面
            initUiDemo();
        } catch (err) {
            console.log(err);
        }
    }

    // 构建场景
    // 流程演示页中去除场景下拉框（流程演示中只加载功能主场景的操作），保留功能下拉框和操作下拉框，去除功能加载按钮
    const doCreateScene = (sfCode) => {
        // 遍历出当前功能的所有场景
        const _sfOprs = ref.current.allOprs.filter(v => v.sfCode == sfCode);
        const _sfScenes = new Set();

        for (const opr of _sfOprs) {
            _sfScenes.add(opr.oprScene);
        }

        ref.current.sfOprs = _sfOprs; // 缓存当前功能的所有操作
        setScenes(Array.from(_sfScenes.values()));
    }

    // 设置第一个场景
    const doSetFirstScene = () => {
        const _sfOprs = ref.current.sfOprs; // 获取当前功能的所有操作

        if (_sfOprs.length > 0) {
            const first = _sfOprs[0];

            doSfMap(first.oprScene);
            setScene(first.oprScene);
            setSceneIndex(0);

            setOprId(first.pkid);
            setOprTitle(first.oprTitle);
            setSfOprs(_sfOprs.filter(v => v.oprScene == first.oprScene));

            initUiDemo();
        } else {
            setScene(null);
            setSceneIndex(0);

            setOprId(null);
            setOprTitle(null);
            setSfOprs([]);

            initUiDemo();
        }
    }

    // 界面流转图
    const doSfMap = (_oprScene) => {
        const sf = ref.current.sf;
        const migrateMap = sf?.migrateMap;

        if (migrateMap) {
            const map = JSON.parse(migrateMap);

            // 解析场景
            let oprScene = _oprScene;
            if (_oprScene) {
                const arr = _oprScene.split('.');
                if (arr.length == 2) {
                    oprScene = arr[1].trim();
                }
            }

            const path = '/' + ref.current.sfName + '/' + oprScene + '/';
            setMapPath(path);
            setSmap(map);
        }
    }

    // 加载界面
    const loadUiDemo = async (sfOpr) => {
        if (!sfOpr) {
            initUiDemo();
            return;
        }

        try {
            ref.current.oprFlag = true;
            const param = { prjId: sfOpr.prjId, sVer: sfOpr.sVer, sfCode: sfOpr.sfCode, oprCode: sfOpr.oprCode };

            // 加载界面
            const data = await runAsync(
                async () => {
                    return await http.get(`/rmsfs/ui/demo`, param);
                }, {
                error: { content: (err) => `查询失败: ${err.message}` },
                loading: { show: true, content: '加载界面信息' },
                success: { show: false },
            });

            const _ui = data.umUi;
            const _ues = data.umUes ?? [];
            const _pues = data.parentUes ?? [];
            const _pics = data.umUiPics ?? [];

            // UI
            if (_ui) {
                setUi(_ui);
                setUes(_ues);
                setPues(_pues);
            }

            // 原型图片
            if (_pics.length > 0) {
                ref.current.searchUi = true; // 找到图片的标记
                setUiPics(_pics);
                setUiPicIndex(0);
            }
        } catch (err) {
            console.log(err);
        }
    }

    // 初始化界面
    const initUiDemo = () => {
        ref.current.oprFlag = false;
        setOprFlag(false);
        setUi({});
        setUes([]);
        setPues([]);
        setUiPics([]);
    }

    // 添加操作前缀
    const doOprPrefix = (oprs) => {
        let sceneNo = null;
        let count = 0;

        for (const opr of oprs) {
            const type = oprType(opr.oprType);

            if (type) {
                opr.oprTitle = type + '：' + opr.oprTitle;
            }

            // 增加前缀
            if (opr.sceneNo) {
                if (opr.sceneNo != sceneNo) {
                    count++;
                }

                // 处理场景
                opr.oprScene = count + '. ' + opr.oprScene;
            }

            sceneNo = opr.sceneNo;
        }
    }

    // 搜索系统功能
    // 选多个功能加载到功能下拉框，清空当前流程及业务作业
    const searchSf = (value) => {
        if (value) {
            const sfIds = value.map(v => v.pkid);
            loadSf(sfIds);
        }
    }

    // 选择功能
    const chgSf = (id, name) => {
        initUiDemo();

        // 设置当前功能
        const index = rmSfs.map(v => v.pkid).indexOf(id);
        const sf = rmSfs[index];

        ref.current.sf = sf;
        ref.current.sfId = id;
        ref.current.sfName = name;

        setSid(id);
        setSname(name);
        setSfIndex(index);

        // 构建场景：设置第一个场景与操作
        doCreateScene(sf.sfCode);
        doSetFirstScene();
    }

    // 选择场景
    const chgScene = (title) => {
        initUiDemo();

        // 设置当前场景
        const index = scenes.indexOf(title);
        setScene(title);
        setSceneIndex(index);

        // 设置当前操作
        const oprs = ref.current.sfOprs.filter(v => v.oprScene == title);

        if (oprs.length > 0) {
            const firstOpr = oprs[0];
            setSfOprs(oprs);
            setOprId(firstOpr.pkid);
            setOprTitle(firstOpr.oprTitle);

            doSfMap(firstOpr.oprScene);
            initUiDemo();
        } else {
            setSfOprs([]);
            setOprId(null);
            setOprTitle(null);
            initUiDemo();
        }
    }

    // 选择操作
    // 当选择第一个操作时，需要加载 “提示界面”
    const chgOpr = (id, title) => {
        const index = sfOprs.map(v => v.pkid).indexOf(id);
        setOprId(id);
        setOprTitle(title);
        setOprIndex(index);
        setOprFlag(false);
        ref.current.searchUi = false;

        if (index <= 0) {
            initUiDemo();
        } else {
            searchUi(index - 1);
        }
    }

    // 寻找UI
    const searchUi = async (index) => {
        for (var i = index; i >= 0; i--) {
            if (!ref.current.searchUi) {
                await loadUiDemo(sfOprs[i]);
            }
        }
    }

    // 执行
    // 加载当前操作执行后的界面（如果当前操作执行会迁移到新界面），并将当前操作的下一操作设置为当前操作
    // 如当前操作是已经最后一步，则回到当前场景第一步操作，载入“提示界面”
    const doOpr = () => {
        // 是否执行标记
        ref.current.oprFlag = true;

        if (null == sfOprs || sfOprs.length == 0) {
            initUiDemo();
            return;
        }

        // 用例的所有操作
        let allOprs = ref.current.allOprs;
        let allSize = allOprs.length;
        let allIndex = allOprs.map(v => v.pkid).indexOf(oprId);

        // 当前场景下操作
        let size = sfOprs.length;
        let index = sfOprs.map(v => v.pkid).indexOf(oprId);

        if (index > -1) {
            // 当前操作
            const sfOpr = sfOprs[index];

            // 1、当操作有图片，展示操作的图片
            const picId = sfOpr.picId;
            const picName = sfOpr.picName;
            if (picId && picId.length > 0 && !ref.current.doOprPicFlag) {
                ref.current.doOprPicFlag = true; // 执行操作图片标记，真实操作放置第2步执行
                ref.current.searchUi = true; // 找到图片的标记
                setUiPics([{ picId, picName }]);
                setUiPicIndex(0);
            } else {
                // 2、执行操作
                ref.current.doOprPicFlag = false;

                // 加载界面：如果当前操作响应指定了界面，则需要迁移到界面原型图
                loadUiDemo(sfOpr);

                // 最后一次执行
                if (index == size - 1) {
                    if (allIndex < allSize - 1) {
                        // 集成用例，持续执行操作，直到最后一个
                        doNextScene();
                    } else {
                        // 当前操作是最后1步，当前操作保持不变，执行按钮灰化
                        setOprFlag(true);
                    }
                } else {
                    // 下一操作
                    const sfOpr = (++index == size) ? sfOprs[0] : sfOprs[index];
                    setOprId(sfOpr.pkid);
                    setOprTitle(sfOpr.oprTitle);
                    setOprIndex(index);
                }
            }

        }
    }

    // 切换到下一个场景
    const doNextScene = () => {
        // 当前场景
        let size = scenes.length;
        let index = scenes.indexOf(scene);

        // 设置当前场景
        const oprScene = (++index == size) ? scenes[0] : scenes[index];

        if (index > -1) {
            setScene(oprScene);
            setSceneIndex(index);

            // 取第一个操作
            const oprs = ref.current.sfOprs.filter(v => v.oprScene == oprScene);

            if (oprs && oprs.length > 0) {
                const firstOpr = oprs[0];
                setOprId(firstOpr.pkid);
                setOprTitle(firstOpr.oprTitle);
                setOprIndex(0);
                setSfOprs(oprs);
            } else {
                setSfOprs([]);
            }
        }
    }

    // 下一场景
    // 将场景下拉框中当前场景的下一场景设置为当前选中场景，并加载选中场景的用户操作和“提示界面”（参见流程加载逻辑）
    // 如当前场景是最后一个，则下一场景按钮灰化
    const nextScene = () => {
        initUiDemo();

        if (null == scenes || scenes.length == 0) {
            return;
        }

        // 当前场景
        let size = scenes.length;
        let index = scenes.indexOf(scene);

        // 下一场景
        if (index > -1) {
            // 设置当前场景
            const oprScene = (++index == size) ? scenes[0] : scenes[index];

            doSfMap(oprScene);
            setScene(oprScene);
            setSceneIndex(index);

            // 取第一个操作
            const oprs = ref.current.sfOprs.filter(v => v.oprScene == oprScene);
            if (oprs && oprs.length > 0) {
                const firstOpr = oprs[0];
                setOprId(firstOpr.pkid);
                setOprTitle(firstOpr.oprTitle);
                setOprIndex(0);
                setSfOprs(oprs);
            } else {
                setSfOprs([]);
            }
        }
    }

    // 下一功能
    // 将功能下拉框中当前功能的下一功能设置为当前选中功能，并加载选中功能的场景、用户操作和“提示界面”（参见流程加载逻辑）
    // 如当前功能是最后一个，则下一功能按钮灰化
    const nextSf = () => {
        initUiDemo();

        if (null == rmSfs || rmSfs.length == 0) {
            return;
        }

        // 当前功能
        let size = rmSfs.length;
        let index = rmSfs.map(v => v.pkid).indexOf(sid);

        // 下一功能
        if (index > -1) {
            const rmSf = (++index == size) ? rmSfs[0] : rmSfs[index];
            ref.current.sf = rmSf;
            ref.current.sfId = rmSf.pkid;
            ref.current.sfName = rmSf.sfName;

            setSid(rmSf.pkid);
            setSname(rmSf.sfName);
            setSfIndex(index);

            // 构建场景：设置第一个场景与操作
            doCreateScene(rmSf.sfCode);
            doSetFirstScene(ref.current.sfOprs);
        } else {
            ref.current.sf = null;
            ref.current.sfId = null;
            ref.current.sfName = null;

            setSid(null);
            setSname(null);
            setSfIndex(0);

            setScene([]);
            setSfOprs([]);
        }
    }

    // 加载界面
    const loadUi = (ui) => {
        loadMap(ui);
    }

    // 加载事件
    const loadUe = (ui, ueCat, ueName) => {
        let res = '界面事件能力/';
        if (ueCat) {
            for (let cat of ueCat.split('@@')) {
                res += cat + '/';
            }
        }
        res += ueName;
        loadMap(ui, res);
    }

    const loadMap = (ui, name) => {
        let path = '/' + ui.uiName + '/';
        if (name) {
            path += name + '/';
        }

        const mapInits = {
            selectedNodes: [{path}],
        }

        window.open("/DpMap?id=" + ui.pkid + "&name=" + ui.uiName + "&type=UI" + "&mapInits=" + JSON.stringify(mapInits), '_blank', winParam);
    }

    // 打开功能
    const openSf = path => {
        const sf = ref.current.sf;
        const sfId = ref.current.sfId;
        const sfName = ref.current.sfName;

        const mapInits = {
            selectedNodes: [{path}],
        }

        if (sfId) {
            const type = sf.ucFlag ? 'UC' : 'SF';
            window.open("/DpMap?id=" + sfId + "&name=" + sfName + "&type=" + type + "&mapInits=" + JSON.stringify(mapInits), '_blank', winParam);
        }
    }

    // 右键执行
    document.onkeydown = function (event) {
        var event = event || window.event;
        if (event.ctrlKey && event.key == 39) {
            doOpr();
        } else if (event.keyCode == 39) {
            let allOprs = ref.current.allOprs;
            let allSize = allOprs.length;
            let allIndex = allOprs.map(v => v.pkid).indexOf(oprId);

            // if (allIndex < allSize - 1) {
            //     doOpr();
            // }
            doOpr();
        }
    }

    return <div style={{ display: 'flex', height: '100%' }}>
        {/* 左侧布局 */}
        <div style={{ borderRight: '1px #f0f2f5 solid', flex: 85 }}>
            {/* 左上侧布局 */}

            {/* 不执行：展示界面流转图 */}
            {
                (ref.current.oprFlag == false) &&
                <div>
                    {
                        smap &&
                        <div>
                            <div style={{ display: 'flex', height: '8vh', fontSize: '20pt', fontWeight: 'bold', justifyContent: 'center', alignItems: 'center', borderBottom: '1px #f0f2f5 solid' }}>
                                集成用例仿真演示
                            </div>

                            <div style={{ borderBottom: '1px #f0f2f5 solid', display: 'flex', height: '80vh', justifyContent: 'center', alignItems: 'center', padding: '10px' }}>
                                <SoftwareMap mapData={smap} style={{ flex: 1, height: '100%' }} inits={mapInits} />
                            </div>
                        </div>
                    }
                </div>
            }

            {/* 执行：展示原型图 */}
            {
                (ref.current.oprFlag == true) &&
                (
                    uiPics.length == 0 ?
                        <div style={{ borderBottom: '1px #f0f2f5 solid', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '88vh', color: '#d9d9d9', fontSize: '14pt' }}>暂无原型图</div>
                        :
                        <div style={{ borderBottom: '1px #f0f2f5 solid', display: 'flex', height: '88vh' }}>
                            <div style={{ display: 'flex', flexGrow: 1, justifyContent: 'center', alignItems: 'center', overflow: 'auto' }}>
                                <img
                                    id={'pic_' + uiPicindex}
                                    title={uiPics[uiPicindex]?.picName}
                                    src={FileUtils.getFileUrl(uiPics[uiPicindex]?.picId)}
                                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                                    onDoubleClick={e => {
                                        const pic = document.getElementById('pic_' + uiPicindex);
                                        const obj = document.createElement('img');

                                        obj.id = 'uiPicindex001';
                                        obj.src = pic.src;
                                        obj.style.maxWidth = '100%';
                                        obj.style.maxHeight = '100%';

                                        document.getElementById('showDic').style.display = 'block';
                                        document.getElementById('showPic').appendChild(obj);
                                    }}
                                />
                            </div>
                        </div>
                )
            }

            {/* 左下侧布局 */}
            <div style={{ display: 'flex', height: '12vh', justifyContent: 'center', alignItems: 'center' }}>
                <table>
                    <UcLayout
                        sf={ref.current.sf}
                        sid={sid}
                        sname={sname}
                        oprId={oprId}
                        scene={scene}
                        rmSfs={rmSfs}
                        scenes={scenes}
                        sfOprs={sfOprs}
                        oprFlag={oprFlag}
                        sfIndex={sfIndex}
                        sceneIndex={sceneIndex}
                        chgSf={chgSf}
                        chgScene={chgScene}
                        chgOpr={chgOpr}
                        nextScene={nextScene}
                        nextSf={nextSf}
                        doOpr={doOpr}
                        openSf={openSf}
                        setVisibleSf={setVisibleSf}
                    />
                </table>
            </div>
        </div>

        {/* 右侧布局 */}{
            ref.current.oprFlag == true &&
            <div style={{ flex: 15, overflow: 'auto' }}>
                {/* 当前界面 */}
                {
                    (ui && ui.uiName) &&
                    <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0px 2px 0px' }}>
                        <div style={{ padding: '4px 0px', width: '159px', background: '#50c3f7', cursor: 'pointer', textAlign: 'center', fontWeight: 'bold' }} onClick={() => loadUi(ui)}>{ui.uiName}</div>
                    </div>
                }
                {
                    ues.map(v => <Ue style={{ display: 'flex', justifyContent: 'center', height: '30px' }} onClick={() => loadUe(ui, v.ueCat, v.ueName)}>{v.ueName}</Ue>)
                }

                {/* 父级界面 */}
                {
                    pues.map(v => {
                        return (
                            <>
                                {
                                    v.seqNo == 1 &&
                                    <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0px 2px 0px' }}>
                                        <div style={{ padding: '4px 0px', width: '159px', background: '#50c3f7', cursor: 'pointer', textAlign: 'center', fontWeight: 'bold' }}
                                            onClick={() => loadUi({ pkid: v.uiId, uiName: v.uiName })}>{v.uiName}</div>
                                    </div>
                                }

                                <Ue style={{ display: 'flex', justifyContent: 'center', height: '30px' }} onClick={() => loadUe({ pkid: v.uiId, uiName: v.uiName }, v.ueCat, v.ueName)}>{v.ueName}</Ue>
                            </>
                        );
                    })
                }
            </div>
        }

        <SelectSfDialog visible={visibleSf} setVisible={setVisibleSf} onCall={searchSf} prjId={ref.current.prjId} sVer={ref.current.sVer} isSingle={false} sfTypeCode="UI" ucFlag={ref.current.sf?.ucFlag} />

        <div
            id="showDic"
            style={{
                display: 'none',
                position: 'absolute',
                left: '0px',
                top: '0px',
                width: '100vw',
                height: '100vh',
                zIndex: '1',
                overflow: 'auto',
                background: '#f0f2f5',

            }}
            onDoubleClick={() => {
                document.getElementById('showPic').innerHTML = '';
                document.getElementById('showDic').style.display = 'none';
            }}
        >
            <div
                id="showPic"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100vw',
                    height: '100vh',
                }}>
            </div>
        </div>
    </div >;

}

export default Pane;
